import React, { useState, useEffect } from "react";
import { Menu, Icon, Badge } from "antd";
import { Link, useLocation } from "react-router-dom";
import { Scrollbars } from "react-custom-scrollbars";
import { useSelector, useDispatch } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { addTag } from "@/store/actions";
import { getMenuItemInMenuListByProperty } from "@/utils";
import { hasPermission } from "@/utils/permission";
import menuList from "@/config/menuConfig";
import "./index.less";

const { SubMenu } = Menu;

// 重新记录数组顺序
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const Meun = () => {
  const [menuTreeNode, setMenuTreeNode] = useState(null);
  const [openKey, setOpenKey] = useState([]);
  
  const location = useLocation();
  const dispatch = useDispatch();
  
  // 从 Redux store 获取数据
  const { role } = useSelector(state => state.user);
  const unreadData = useSelector(state => state.message?.unreadData);

  // 获取未读消息数
  const getUnreadCount = () => {
    if (unreadData && typeof unreadData === 'object') {
      const values = Object.values(unreadData);
      const count = values.reduce((acc, item) => acc + (item.counter || 0), 0);
      return count;
    }
    return 0;
  };

  // 优化的菜单项权限过滤
  const filterMenuItem = (item) => {
    const { roles } = item;
    const hasAccess = hasPermission(role, roles);
    
    // 如果当前用户没有权限访问此菜单项
    if (!hasAccess) {
      // 检查是否有子菜单的权限
      if (item.children && item.children.length > 0) {
        const accessibleChildren = item.children.filter(child => hasPermission(role, child.roles));
        return accessibleChildren.length > 0;
      }
      return false;
    }
    
    return true;
  };

  // 查找当前路径需要打开的菜单项
  const findOpenKeys = (menuList, currentPath) => {
    const openKeys = [];
    
    const findKeys = (items, parentPath = '') => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          const accessibleChildren = item.children.filter(child => hasPermission(role, child.roles));
          const cItem = accessibleChildren.find(child => currentPath.indexOf(child.path) === 0);
          
          if (cItem) {
            openKeys.push(item.path);
          }
          
          findKeys(accessibleChildren, item.path);
        }
      });
    };
    
    findKeys(menuList);
    return openKeys;
  };

  // 菜单渲染 - 移除setState调用
  const getMenuNodes = (menuList) => {
    return menuList.reduce((pre, item) => {
      if (filterMenuItem(item)) {
        if (!item.children) {
          const unreadCount = item.path === '/message-center' ? getUnreadCount() : 0;
          
          pre.push(
            <Menu.Item key={item.path} style={{ position: 'relative' }}>
              <Link to={item.path}>
                {item.icon ? <Icon type={item.icon} /> : null}
                <span>
                  {item.title}
                  {item.path === '/message-center' && unreadCount > 0 && (
                    <Badge 
                      count={unreadCount}
                      style={{
                        position: 'absolute',
                        right: '-83px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1
                      }}
                    />
                  )}
                </span>
              </Link>
            </Menu.Item>
          );
        } else {
          // 过滤子菜单
          const accessibleChildren = item.children.filter(child => hasPermission(role, child.roles));
          
          if (accessibleChildren.length === 0) {
            return pre;
          }

          // 向pre添加<SubMenu>
          pre.push(
            <SubMenu
              key={item.path}
              title={
                <span>
                  {item.icon ? <Icon type={item.icon} /> : null}
                  <span>{item.title}</span>
                </span>
              }
            >
              {getMenuNodes(accessibleChildren)}
            </SubMenu>
          );
        }
      }
      return pre;
    }, []);
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const items = reorder(
      menuTreeNode,
      result.source.index,
      result.destination.index
    );
    setMenuTreeNode(items);
  };

  const handleMenuSelect = ({ key = "/dashboard" }) => {
    let menuItem = getMenuItemInMenuListByProperty(menuList, "path", key);
    dispatch(addTag(menuItem));
  };

  // 路径变化时更新openKey
  useEffect(() => {
    if (role) {
      const newOpenKeys = findOpenKeys(menuList, location.pathname);
      setOpenKey(newOpenKeys);
    }
  }, [location.pathname, role]);

  // 初始化菜单 - 只在role变化时重新生成
  useEffect(() => {
    if (role) {
      const nodes = getMenuNodes(menuList);
      setMenuTreeNode(nodes);
    }
  }, [role]); // 只依赖role

  // 处理未读消息数更新 - 只更新相关菜单项
  useEffect(() => {
    if (menuTreeNode && role && unreadData) {
      // 只有当未读消息数发生变化时才重新生成菜单
      const nodes = getMenuNodes(menuList);
      setMenuTreeNode(nodes);
    }
  }, [unreadData]);

  // 如果没有角色信息，不显示菜单
  if (!role || !menuTreeNode) {
    return (
      <div className="sidebar-menu-container">
        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
          加载菜单中...
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar-menu-container">
      <Scrollbars autoHide autoHideTimeout={1000} autoHideDuration={200}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {menuTreeNode.map((item, index) => (
                  <Draggable
                    key={item.key}
                    draggableId={item.key}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <Menu
                          mode="inline"
                          theme="light"
                          onSelect={handleMenuSelect}
                          selectedKeys={[location.pathname]}
                          defaultOpenKeys={openKey}
                        >
                          {item}
                        </Menu>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Scrollbars>
    </div>
  );
};

export default Meun;
